<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import KanbanAPI from 'dashboard/api/kanban';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  conversation: {
    type: Object,
    default: () => ({}),
  },
  mapping: {
    type: Object,
    default: () => ({}),
  },
  funnel: {
    type: Object,
    default: () => ({}),
  },
  // Histórico usa a mesma timeline em modo leitura (sem campo de nota).
  hideComposer: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const activities = ref([]);
const isLoadingActivities = ref(false);
const newNote = ref('');
const isAddingNote = ref(false);

// Normalizes date values coming either as Unix seconds (numbers) or
// ISO8601 strings — mirrors DealInsights.vue's normalizeDate helper.
const normalizeDate = value => {
  if (!value) return null;
  if (typeof value === 'number' && value < 4102444800) {
    return new Date(value * 1000);
  }
  return new Date(value);
};

const formatRelativeTime = value => {
  const date = normalizeDate(value);
  if (!date || Number.isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('KANBAN.DEAL.TIME.JUST_NOW');
  if (diffMins < 60)
    return t('KANBAN.DEAL.TIME.MINUTES_AGO', { count: diffMins });
  if (diffHours < 24)
    return t('KANBAN.DEAL.TIME.HOURS_AGO', { count: diffHours });
  if (diffDays < 7) return t('KANBAN.DEAL.TIME.DAYS_AGO', { count: diffDays });
  return date.toLocaleDateString('pt-BR');
};

// Icon per `activity_type` — mirrors the mapping used by the previous
// DealActivityPanel/DealDetailsSidebar implementations (FunnelActivity::ACTIVITY_TYPES).
const ACTIVITY_ICONS = {
  deal_created: 'i-lucide-plus-circle',
  deal_won: 'i-lucide-trophy',
  deal_lost: 'i-lucide-x-circle',
  deal_reopened: 'i-lucide-refresh-cw',
  stage_changed: 'i-lucide-arrow-right',
  value_updated: 'i-lucide-dollar-sign',
  probability_updated: 'i-lucide-percent',
  expected_close_updated: 'i-lucide-calendar',
  note_added: 'i-lucide-sticky-note',
  checklist_updated: 'i-lucide-check-circle',
  agent_assigned: 'i-lucide-user-plus',
  agent_removed: 'i-lucide-user-minus',
  ai_stage_changed: 'i-lucide-sparkles',
  ai_stage_suggested: 'i-lucide-sparkles',
  ai_suggestion_accepted: 'i-lucide-sparkles',
  ai_suggestion_rejected: 'i-lucide-sparkles',
  follow_up_sent: 'i-lucide-send',
};

const activityIcon = type => ACTIVITY_ICONS[type] || 'i-lucide-info';

const isNoteActivity = activity => activity.activity_type === 'note_added';

const activityText = activity =>
  (isNoteActivity(activity) && activity.metadata?.content) ||
  activity.description ||
  activity.activity_type;

const activityMeta = activity => {
  const author = activity.user_name || t('DEAL_RECORD.ACTIVITIES.SYSTEM');
  const when = formatRelativeTime(activity.created_at);
  return when ? `${author} · ${when}` : author;
};

// Mais recentes no topo (a API devolve em ordem crescente).
const sortedActivities = computed(() =>
  [...activities.value].sort(
    (a, b) =>
      (normalizeDate(b.created_at)?.getTime() || 0) -
      (normalizeDate(a.created_at)?.getTime() || 0)
  )
);

const loadActivities = async () => {
  if (!props.funnel?.id || !props.mapping?.id) return;
  isLoadingActivities.value = true;
  try {
    const response = await KanbanAPI.getDealActivities(
      props.funnel.id,
      props.conversation.id
    );
    activities.value = response.data?.activities || [];
  } catch {
    // Silent fail — the activity timeline is a nice-to-have, not core flow.
  } finally {
    isLoadingActivities.value = false;
  }
};

const addNote = async () => {
  if (!newNote.value.trim() || !props.funnel?.id || isAddingNote.value) return;
  isAddingNote.value = true;
  try {
    await KanbanAPI.addDealNote(
      props.funnel.id,
      props.conversation.id,
      newNote.value
    );
    newNote.value = '';
    useAlert(t('KANBAN.DEAL.NOTE_ADDED'));
    emit('update');
    await loadActivities();
  } catch {
    useAlert(t('KANBAN.DEAL.NOTE_ADD_ERROR'));
  } finally {
    isAddingNote.value = false;
  }
};

onMounted(loadActivities);
</script>

<template>
  <CardLayout>
    <h3
      class="flex items-center gap-1.5 text-[13px] font-semibold text-n-slate-12"
    >
      <span class="i-lucide-history text-sm text-n-slate-10" />
      {{ t('DEAL_RECORD.ACTIVITIES.TITLE') }}
    </h3>

    <div v-if="!hideComposer" class="flex flex-col w-full gap-2">
      <textarea
        v-model="newNote"
        rows="2"
        :placeholder="t('KANBAN.DEAL.DETAILS.ADD_NOTE_PLACEHOLDER')"
        class="w-full resize-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 placeholder-n-slate-10 outline-none transition-colors focus:border-n-blue-7"
      />
      <Button
        class="self-end"
        size="xs"
        icon="i-lucide-plus"
        :label="t('KANBAN.DEAL.DETAILS.ADD_NOTE')"
        :is-loading="isAddingNote"
        :disabled="!newNote.trim()"
        @click="addNote"
      />
    </div>

    <div v-if="isLoadingActivities" class="flex justify-center w-full py-3">
      <Icon
        icon="i-lucide-loader-2"
        class="size-4 animate-spin text-n-slate-10"
      />
    </div>

    <ul v-else-if="activities.length" class="flex flex-col w-full gap-3">
      <li
        v-for="activity in sortedActivities"
        :key="activity.id"
        class="flex items-start w-full gap-2.5"
      >
        <span
          class="flex items-center justify-center mt-0.5 rounded-full shrink-0 size-6 bg-n-alpha-2"
        >
          <Icon
            :icon="activityIcon(activity.activity_type)"
            class="size-3.5 text-n-slate-11"
          />
        </span>
        <div class="flex flex-col min-w-0">
          <p class="text-sm break-words text-n-slate-12">
            {{ activityText(activity) }}
          </p>
          <span class="text-xs text-n-slate-10">
            {{ activityMeta(activity) }}
          </span>
        </div>
      </li>
    </ul>

    <p v-else class="text-sm text-n-slate-10">
      {{ t('KANBAN.DEAL.HISTORY.EMPTY') }}
    </p>
  </CardLayout>
</template>
